import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface ColorSwatchProps {
  name: string;
  variable: string;
  hsl: string;
  hex?: string;
  rgb?: string;
  usage: string;
}

export const ColorSwatch = ({ name, variable, hsl, hex, rgb, usage }: ColorSwatchProps) => {
  const [copied, setCopied] = useState<string | null>(null);

  const copyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    toast.success(`${type} copiado al portapapeles`);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-all duration-300">
      <div 
        className="h-32 w-full border-b"
        style={{ backgroundColor: `hsl(${hsl})` }}
      />
      <div className="p-4 space-y-3">
        <div>
          <h3 className="font-semibold text-foreground">{name}</h3>
          <p className="text-xs text-muted-foreground mt-1">{usage}</p>
        </div>
        
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <code className="text-xs bg-muted px-2 py-1 rounded flex-1">
              {variable}
            </code>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0"
              onClick={() => copyToClipboard(`var(${variable})`, "Variable")}
            >
              {copied === "Variable" ? (
                <Check className="h-3 w-3 text-viable-green" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
            </Button>
          </div>

          <div className="flex items-center justify-between gap-2">
            <code className="text-xs bg-muted px-2 py-1 rounded flex-1">
              hsl({hsl})
            </code>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0"
              onClick={() => copyToClipboard(`hsl(${hsl})`, "HSL")}
            >
              {copied === "HSL" ? (
                <Check className="h-3 w-3 text-viable-green" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
            </Button>
          </div>

          {hex && (
            <div className="flex items-center justify-between gap-2">
              <code className="text-xs bg-muted px-2 py-1 rounded flex-1">
                {hex}
              </code>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0"
                onClick={() => copyToClipboard(hex, "HEX")}
              >
                {copied === "HEX" ? (
                  <Check className="h-3 w-3 text-viable-green" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
              </Button>
            </div>
          )}

          {rgb && (
            <div className="flex items-center justify-between gap-2">
              <code className="text-xs bg-muted px-2 py-1 rounded flex-1">
                {rgb}
              </code>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0"
                onClick={() => copyToClipboard(rgb, "RGB")}
              >
                {copied === "RGB" ? (
                  <Check className="h-3 w-3 text-viable-green" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
